import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { GameCard } from "@/components/GameCard";
import { viewerApi, API } from "@/lib/api";
import { toast, Toaster } from "@/components/ui/sonner";
import { ArrowLeft, Radio, Clock, Maximize, Minimize, Share2, Eye } from "lucide-react";

function formatTime(iso) {
  if (!iso) return "TBD";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function computeClock(game) {
  let s = game.clock_seconds || 0;
  if (game.clock_running && game.clock_updated_at) {
    const elapsed = (Date.now() - new Date(game.clock_updated_at).getTime()) / 1000;
    s = Math.max(0, s - elapsed);
  }
  return Math.round(s);
}

function mmss(total) {
  const s = Math.max(0, Math.floor(total));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

export default function Watch() {
  const { id } = useParams();
  const [game, setGame] = useState(null);
  const [more, setMore] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [clock, setClock] = useState(0);
  const [viewers, setViewers] = useState(0);
  const [isFs, setIsFs] = useState(false);
  const gameRef = useRef(null);

  useEffect(() => {
    const onFs = () => {
      const fsEl = document.fullscreenElement || document.webkitFullscreenElement;
      const frame = document.getElementById("player-frame");
      if (!fsEl) {
        setIsFs(false);
        return;
      }
      // If the Cloudflare iframe entered native fullscreen, redirect to our
      // wrapper so the logo + scoreboard overlays stay visible.
      if (frame && fsEl !== frame && frame.contains(fsEl)) {
        const doExit = document.exitFullscreen || document.webkitExitFullscreen;
        Promise.resolve(doExit && doExit.call(document))
          .catch(() => {})
          .finally(() => {
            setIsFs(true);
            const req = frame.requestFullscreen || frame.webkitRequestFullscreen;
            if (req) {
              try {
                req.call(frame);
              } catch (e) {}
            }
          });
      } else if (fsEl === frame) {
        setIsFs(true);
      }
    };
    document.addEventListener("fullscreenchange", onFs);
    document.addEventListener("webkitfullscreenchange", onFs);
    return () => {
      document.removeEventListener("fullscreenchange", onFs);
      document.removeEventListener("webkitfullscreenchange", onFs);
    };
  }, []);

  // Viewer heartbeat while watching
  useEffect(() => {
    let sid = sessionStorage.getItem("ysv_sid");
    if (!sid) {
      sid = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      sessionStorage.setItem("ysv_sid", sid);
    }
    let active = true;
    const beat = async () => {
      try {
        const { data } = await viewerApi.post(`/games/${id}/heartbeat`, { session_id: sid });
        if (active) setViewers(data.count);
      } catch {}
    };
    beat();
    const t = setInterval(beat, 12000);
    return () => {
      active = false;
      clearInterval(t);
    };
  }, [id]);

  useEffect(() => {
    setGame(null);
    setNotFound(false);
    viewerApi
      .get(`/games/${id}`)
      .then(({ data }) => {
        setGame(data);
        gameRef.current = data;
        setClock(computeClock(data));
      })
      .catch(() => setNotFound(true));
    viewerApi.get("/games").then(({ data }) => setMore(data.filter((g) => g.id !== id).slice(0, 3)));
  }, [id]);

  // Poll for live scoreboard/status updates
  useEffect(() => {
    const poll = setInterval(async () => {
      try {
        const { data } = await viewerApi.get(`/games/${id}`);
        setGame(data);
        gameRef.current = data;
        setClock(computeClock(data));
      } catch {}
    }, 8000);
    return () => clearInterval(poll);
  }, [id]);

  // Local 1s clock tick
  useEffect(() => {
    const t = setInterval(() => {
      const g = gameRef.current;
      if (g && g.clock_running) setClock((c) => Math.max(0, c - 1));
    }, 1000);
    return () => clearInterval(t);
  }, []);

  const goFullscreen = () => {
    if (isFs) {
      const doExit = document.exitFullscreen || document.webkitExitFullscreen;
      if ((document.fullscreenElement || document.webkitFullscreenElement) && doExit) {
        try {
          doExit.call(document);
        } catch (e) {}
      }
      setIsFs(false);
      return;
    }
    const el = document.getElementById("player-frame");
    if (!el) return;
    setIsFs(true); // CSS pseudo-fullscreen (works on iOS / mobile, keeps overlays)
    const req = el.requestFullscreen || el.webkitRequestFullscreen;
    if (req) {
      try {
        req.call(el);
      } catch (e) {}
    }
  };

  const share = async () => {
    const url = `${API}/share/${id}`;
    const title = game ? `${game.team_a} vs ${game.team_b} | YSVLive` : "YSVLive";
    try {
      if (navigator.share) {
        await navigator.share({ title, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Share link copied to clipboard");
      }
    } catch {
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Share link copied to clipboard");
      } catch {
        toast.error("Could not copy link");
      }
    }
  };

  return (
    <div className="App">
      <Toaster />
      <Header />
      <div className="mx-auto max-w-[1400px] px-6 py-6">
        <Link
          to="/"
          data-testid="back-link"
          className="mb-5 inline-flex items-center gap-2 font-display text-sm font-bold uppercase tracking-widest text-white/50 transition-colors hover:text-white"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </Link>

        {notFound ? (
          <p className="py-20 text-center font-display text-2xl uppercase tracking-widest text-white/40">
            Game not found
          </p>
        ) : !game ? (
          <p className="py-20 text-center font-display text-xl uppercase tracking-widest text-white/40">
            Loading…
          </p>
        ) : (
          <>
            {/* Player */}
            <div
              id="player-frame"
              data-testid="video-player"
              className={`overflow-hidden border border-white/10 bg-black ${isFs ? "fixed inset-0 z-[9999] h-screen w-screen border-0" : "relative aspect-video w-full"}`}
            >
              {game.embed_src ? (
                <iframe
                  src={game.embed_src}
                  title={`${game.team_a} vs ${game.team_b}`}
                  className="h-full w-full"
                  allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                  allowFullScreen
                />
              ) : (
                <div className="flex h-full w-full flex-col items-center justify-center gap-3 text-white/40">
                  <Radio className="h-10 w-10" />
                  <p className="font-display uppercase tracking-widest">Stream not yet available</p>
                </div>
              )}
              {/* Exit fullscreen (visible only in fullscreen) */}
              {isFs && (
                <button
                  onClick={goFullscreen}
                  data-testid="exit-fullscreen-button"
                  className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center bg-[#0A0A0A]/80 text-white backdrop-blur transition-colors hover:bg-[#007AFF]"
                  aria-label="Exit fullscreen"
                >
                  <Minimize className="h-5 w-5" />
                </button>
              )}
              {/* Broadcast logo (top-left, opposite viewer count) */}
              <img
                src="/ysv-logo.png"
                alt="YSV"
                data-testid="player-logo"
                className={`pointer-events-none absolute left-4 top-4 opacity-95 drop-shadow-[0_2px_6px_rgba(0,0,0,0.8)] ${isFs ? "h-14" : "h-7 md:h-9"}`}
              />
              {/* Live viewer count (top-right) */}
              {game.status === "live" && viewers > 0 && (
                <div
                  data-testid="viewer-badge"
                  className={`pointer-events-none absolute flex items-center gap-1.5 border border-white/15 bg-[#0A0A0A]/80 font-display font-bold uppercase tracking-widest text-white backdrop-blur ${isFs ? "right-16 top-4 px-4 py-2 text-lg" : "right-4 top-4 px-3 py-1.5 text-sm"}`}
                >
                  <Eye className={isFs ? "h-5 w-5 text-[#007AFF]" : "h-4 w-4 text-[#007AFF]"} />
                  {viewers.toLocaleString()} Watching
                </div>
              )}
              {/* Scoreboard (bottom-center) */}
              {game.scoreboard_enabled && (
                <div
                  data-testid="scoreboard-overlay"
                  className={`pointer-events-none absolute left-1/2 flex -translate-x-1/2 items-stretch border-2 border-white/20 bg-[#0A0A0A]/90 shadow-2xl backdrop-blur-md ${isFs ? "bottom-12" : "bottom-5"}`}
                >
                  <OverlayTeam name={game.team_a} logo={game.team_a_logo} color={game.team_a_color} score={game.score_a} big={isFs} />
                  <div className={`flex flex-col items-center justify-center border-x border-white/20 ${isFs ? "min-w-[80px] px-3 py-1.5" : "min-w-[90px] px-4 py-2 md:min-w-[120px]"}`}>
                    {game.period && (
                      <span className={`font-display font-bold uppercase tracking-[0.2em] text-[#007AFF] ${isFs ? "text-xs" : "text-xs md:text-sm"}`}>
                        {game.period}
                      </span>
                    )}
                    <span data-testid="overlay-clock" className={`font-display font-black tabular-nums leading-none text-white ${isFs ? "text-3xl" : "text-3xl md:text-5xl"}`}>
                      {mmss(clock)}
                    </span>
                  </div>
                  <OverlayTeam name={game.team_b} logo={game.team_b_logo} color={game.team_b_color} score={game.score_b} align="right" big={isFs} />
                </div>
              )}
            </div>

            {/* Title / Scoreboard */}
            <div className="mt-6 flex flex-col gap-4 border-b border-white/10 pb-6 md:flex-row md:items-end md:justify-between">
              <div>
                <div className="mb-3 flex items-center gap-3">
                  {game.status === "live" && (
                    <span className="flex items-center gap-1.5 bg-[#007AFF] px-2.5 py-1">
                      <span className="relative flex h-2 w-2">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
                        <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
                      </span>
                      <span className="font-display text-xs font-bold uppercase tracking-widest text-white">
                        Live
                      </span>
                    </span>
                  )}
                  {game.league && (
                    <span className="font-display text-xs font-bold uppercase tracking-[0.2em] text-[#007AFF]">
                      {game.league}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-4">
                  <WatchTeam name={game.team_a} logo={game.team_a_logo} color={game.team_a_color} />
                  <span className="font-display text-3xl font-black uppercase text-white/30 md:text-4xl">vs</span>
                  <WatchTeam name={game.team_b} logo={game.team_b_logo} color={game.team_b_color} />
                </div>
                <div className="mt-3 flex items-center gap-1.5 text-white/50">
                  <Clock className="h-4 w-4" />
                  <span>{formatTime(game.start_time)}</span>
                </div>
              </div>
              {game.embed_src && (
                <div className="flex gap-2">
                  <button
                    onClick={share}
                    data-testid="share-button"
                    className="inline-flex items-center gap-2 border border-white/20 px-6 py-3 font-display text-sm font-bold uppercase tracking-widest text-white transition-colors hover:bg-[#007AFF] focus:outline-none focus:ring-2 focus:ring-[#007AFF]"
                  >
                    <Share2 className="h-4 w-4" /> Share
                  </button>
                  <button
                    onClick={goFullscreen}
                    data-testid="fullscreen-button"
                    className="inline-flex items-center gap-2 border border-white/20 px-6 py-3 font-display text-sm font-bold uppercase tracking-widest text-white transition-colors hover:bg-[#007AFF] focus:outline-none focus:ring-2 focus:ring-[#007AFF]"
                  >
                    <Maximize className="h-4 w-4" /> Fullscreen
                  </button>
                </div>
              )}
            </div>

            {game.description && (
              <p className="mt-6 max-w-3xl leading-relaxed text-white/70">{game.description}</p>
            )}

            {more.length > 0 && (
              <div className="mt-14">
                <h2 className="mb-6 font-display text-2xl font-bold uppercase tracking-tight text-white">
                  More Games
                </h2>
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                  {more.map((g) => (
                    <GameCard key={g.id} game={g} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const OverlayTeam = ({ name, logo, color, score, align, big }) => (
  <div className={`flex items-center ${big ? "gap-2 px-4 py-2" : "gap-3 px-5 py-2.5"} ${align === "right" ? "flex-row-reverse" : ""}`}>
    {logo ? (
      <img src={logo} alt="" className={`object-contain ${big ? "h-8 w-8" : "h-9 w-9 md:h-11 md:w-11"}`} onError={(e) => (e.currentTarget.style.display = "none")} />
    ) : (
      <span className={big ? "h-7 w-1.5" : "h-8 w-2 md:h-10"} style={{ backgroundColor: color || "#007AFF" }} />
    )}
    <span className={`font-display font-bold uppercase tracking-wider text-white/90 ${big ? "text-sm" : "text-sm md:text-lg"}`}>
      {(name || "").slice(0, 5)}
    </span>
    <span className={`font-display font-black tabular-nums leading-none text-white ${big ? "text-3xl" : "text-3xl md:text-5xl"}`}>{score ?? 0}</span>
  </div>
);


const WatchTeam = ({ name, logo, color }) => (
  <div className="flex items-center gap-3">
    {logo ? (
      <img
        src={logo}
        alt={name}
        className="h-12 w-12 shrink-0 object-contain md:h-16 md:w-16"
        onError={(e) => {
          e.currentTarget.style.display = "none";
        }}
      />
    ) : (
      <span
        className="flex h-12 w-12 shrink-0 items-center justify-center font-display text-xl font-black text-white md:h-16 md:w-16 md:text-2xl"
        style={{ backgroundColor: color || "#1F1F1F" }}
      >
        {(name || "?").slice(0, 1).toUpperCase()}
      </span>
    )}
    <span
      className="font-display text-4xl font-black uppercase leading-none tracking-tighter md:text-6xl"
      style={{ color: color || "#FFFFFF" }}
    >
      {name}
    </span>
  </div>
);
